import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Sunrise, Sun, Sunset, Moon, PlaneTakeoff, Info } from "lucide-react";
import { Header } from "../components/Layout";
import { SearchWidget } from "../components/SearchWidget";
import { FlightCard } from "../components/FlightCard";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { Skeleton } from "../components/ui/skeleton";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { http, money, dateLabel, errMsg } from "../lib/api";

const SLOTS = [
  { id: "early", label: "Before 6 AM", icon: Sunrise, test: (h) => h < 6 },
  { id: "morning", label: "6 AM - 12 PM", icon: Sun, test: (h) => h >= 6 && h < 12 },
  { id: "afternoon", label: "12 PM - 6 PM", icon: Sunset, test: (h) => h >= 12 && h < 18 },
  { id: "night", label: "After 6 PM", icon: Moon, test: (h) => h >= 18 },
];
const SORTS = [["cheapest", "Cheapest"], ["fastest", "Fastest"], ["earliest", "Early departure"], ["latest", "Late departure"]];

const Filters = ({ offers, f, setF, maxPrice, minPrice }) => {
  const airlines = [...new Map(offers.map((o) => [o.airline_code, o.airline_name])).entries()];
  const toggle = (k, v) => setF({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] });
  return (
    <div className="space-y-6" data-testid="filters-panel">
      <div>
        <div className="font-heading font-bold mb-3">Stops</div>
        <div className="grid grid-cols-2 gap-2">
          {[[0, "Non-stop"], [1, "1 Stop"]].map(([v, l]) => (
            <button key={v} data-testid={`filter-stops-${v}`} onClick={() => toggle("stops", v)}
              className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${f.stops.includes(v) ? "border-[#EC5B24] bg-orange-50 text-[#EC5B24]" : "border-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="font-heading font-bold mb-3">Departure time</div>
        <div className="grid grid-cols-2 gap-2">
          {SLOTS.map((s) => (
            <button key={s.id} data-testid={`filter-time-${s.id}`} onClick={() => toggle("slots", s.id)}
              className={`rounded-xl border px-2 py-2 text-xs font-medium flex flex-col items-center gap-1 transition-colors ${f.slots.includes(s.id) ? "border-[#EC5B24] bg-orange-50 text-[#EC5B24]" : "border-slate-200"}`}>
              <s.icon className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="font-heading font-bold mb-3 flex justify-between">Max price <span className="font-mono text-sm text-[#EC5B24]" data-testid="filter-price-value">{money(f.price ?? maxPrice)}</span></div>
        <Slider data-testid="filter-price-slider" min={minPrice} max={maxPrice} step={100} value={[f.price ?? maxPrice]} onValueChange={([v]) => setF({ ...f, price: v })} />
      </div>
      <div>
        <div className="font-heading font-bold mb-3">Airlines</div>
        <div className="space-y-2.5">
          {airlines.map(([code, name]) => (
            <label key={code} className="flex items-center gap-2.5 text-sm cursor-pointer">
              <Checkbox data-testid={`filter-airline-${code}`} checked={f.airlines.includes(code)} onCheckedChange={() => toggle("airlines", code)} />
              {name}
            </label>
          ))}
        </div>
      </div>
      <button data-testid="filters-reset-btn" onClick={() => setF({ stops: [], slots: [], airlines: [], price: null })} className="text-sm font-semibold text-[#EC5B24]">Reset all</button>
    </div>
  );
};

export default function Results() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const q = Object.fromEntries(params.entries());
  const pax = { adults: +q.adults || 1, children: +q.children || 0, infants: +q.infants || 0 };
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cal, setCal] = useState([]);
  const [sort, setSort] = useState("cheapest");
  const [f, setF] = useState({ stops: [], slots: [], airlines: [], price: null });

  useEffect(() => {
    setData(null); setError(""); setF({ stops: [], slots: [], airlines: [], price: null });
    http.post("/flights/search", { origin: q.from, destination: q.to, depart_date: q.date, return_date: q.ret || null, ...pax, cabin: q.cabin || "economy" })
      .then((r) => setData(r.data)).catch((e) => setError(errMsg(e, "Could not load flights")));
    http.get("/flights/fare-calendar", { params: { origin: q.from, destination: q.to, date: q.date, cabin: q.cabin } }).then((r) => setCal(r.data)).catch(() => setCal([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  const offers = data?.offers || [];
  const maxPrice = Math.max(1000, ...offers.map((o) => o.price_per_adult));
  const minPrice = offers.length ? Math.min(...offers.map((o) => o.price_per_adult)) : 0;
  const list = useMemo(() => {
    const r = offers.filter((o) => {
      const s = o.slices[0];
      const h = +s.depart_at.slice(11, 13);
      return (!f.stops.length || f.stops.includes(Math.min(s.stops, 1))) &&
        (!f.slots.length || SLOTS.some((x) => f.slots.includes(x.id) && x.test(h))) &&
        (!f.airlines.length || f.airlines.includes(o.airline_code)) &&
        (f.price == null || o.price_per_adult <= f.price);
    });
    const key = { cheapest: (o) => o.price_per_adult, fastest: (o) => o.slices[0].duration_min, earliest: (o) => o.slices[0].depart_at, latest: (o) => o.slices[0].depart_at };
    r.sort((a, b) => (key[sort](a) > key[sort](b) ? 1 : -1) * (sort === "latest" ? -1 : 1));
    return r;
  }, [offers, f, sort]);
  const cheapestId = offers.length ? [...offers].sort((a, b) => a.price_per_adult - b.price_per_adult)[0].id : null;
  const fastestId = offers.length ? [...offers].sort((a, b) => a.slices[0].duration_min - b.slices[0].duration_min)[0].id : null;

  const initial = {
    from: { code: q.from, city: q.fromCity || q.from, name: "" }, to: { code: q.to, city: q.toCity || q.to, name: "" },
    date: q.date, ret: q.ret, pax, cabin: q.cabin,
  };

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <div className="bg-[#1C1A4A] py-4">
        <div className="mx-auto max-w-7xl px-4"><SearchWidget key={params.toString()} initial={initial} compact /></div>
      </div>
      <div className="mx-auto max-w-7xl px-4 py-6 grid gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block bg-white rounded-2xl p-5 h-fit sticky top-20 border border-slate-100">
          <div className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Filters</div>
          <Filters offers={offers} f={f} setF={setF} maxPrice={maxPrice} minPrice={minPrice} />
        </aside>
        <main className="min-w-0">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900" data-testid="results-title">{q.fromCity || q.from} → {q.toCity || q.to}</h1>
              <div className="text-sm text-slate-500">{dateLabel(q.date)}{q.ret ? ` – ${dateLabel(q.ret)}` : ""} · {pax.adults + pax.children + pax.infants} traveller(s)</div>
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="mobile-filters-btn" className="lg:hidden flex items-center gap-1.5 rounded-full border bg-white px-4 py-2 text-sm font-semibold"><SlidersHorizontal className="h-4 w-4" /> Filters</button>
              </SheetTrigger>
              <SheetContent side="left" className="overflow-y-auto"><Filters offers={offers} f={f} setF={setF} maxPrice={maxPrice} minPrice={minPrice} /></SheetContent>
            </Sheet>
          </div>

          {!q.ret && cal.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4" data-testid="fare-calendar">
              {cal.map((c) => (
                <button key={c.date} data-testid={`fare-date-${c.date}`} onClick={() => { params.set("date", c.date); setParams(params); }}
                  className={`shrink-0 rounded-xl border px-4 py-2 text-center min-w-[104px] transition-colors ${c.date === q.date ? "bg-[#1C1A4A] text-white border-[#1C1A4A]" : "bg-white border-slate-200 hover:border-[#EC5B24]"}`}>
                  <div className="text-xs">{dateLabel(c.date)}</div>
                  <div className={`font-mono text-sm font-bold ${c.date === q.date ? "text-white" : "text-[#0F9D58]"}`}>{money(c.min_price)}</div>
                </button>
              ))}
            </div>
          )}

          {data?.source === "sample" && (
            <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 mb-4" data-testid="sample-data-banner">
              <Info className="h-4 w-4 shrink-0" /> Showing sample fares. Live airline inventory turns on once a flight data key is connected.
            </div>
          )}
          {data?.source === "serpapi" && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-white border border-slate-100 text-sm px-4 py-3 mb-4" data-testid="live-fares-banner">
              <span className="flex items-center gap-1.5 font-semibold text-[#0F9D58]"><span className="h-2 w-2 rounded-full bg-[#0F9D58] animate-pulse" /> Live fares</span>
              {data.price_insights?.price_level && (
                <span className="text-slate-600" data-testid="price-insight">
                  Prices are <b className={data.price_insights.price_level === "low" ? "text-[#0F9D58]" : data.price_insights.price_level === "high" ? "text-red-500" : "text-slate-900"}>{data.price_insights.price_level}</b> right now
                  {data.price_insights.typical_price_range && <> · usually {money(data.price_insights.typical_price_range[0])} – {money(data.price_insights.typical_price_range[1])}</>}
                </span>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 bg-white rounded-2xl p-1.5 border border-slate-100">
            {SORTS.map(([id, l]) => (
              <button key={id} data-testid={`sort-${id}`} onClick={() => setSort(id)}
                className={`rounded-xl py-2 text-sm font-semibold transition-colors ${sort === id ? "bg-orange-50 text-[#EC5B24]" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>

          {error && <div className="bg-white rounded-2xl p-10 text-center text-red-600" data-testid="results-error">{error}</div>}
          {!data && !error && <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-36 rounded-2xl" />)}</div>}
          {data && (
            <>
              <div className="text-sm text-slate-500 mb-3" data-testid="results-count">{list.length} flights found</div>
              <div className="space-y-3">
                {list.map((o) => (
                  <FlightCard key={o.id} offer={o} cheapest={o.id === cheapestId} fastest={o.id === fastestId}
                    priceLabel={o.needs_return ? "per traveller · round trip" : "per traveller"}
                    onBook={() => navigate(`/book/${o.id}`)} />
                ))}
              </div>
              {!list.length && (
                <div className="bg-white rounded-2xl p-12 text-center" data-testid="no-results">
                  <PlaneTakeoff className="h-10 w-10 mx-auto text-slate-300" />
                  <div className="font-heading font-bold mt-3">No flights match your filters</div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
